import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';

const ErrorNotificationContext = createContext();

// Global error handler that can be accessed by axios interceptors
let globalErrorHandler = null;

export const setGlobalErrorHandler = (handler) => {
    globalErrorHandler = handler;
};

export const getGlobalErrorHandler = () => {
    return globalErrorHandler;
};

export const useErrorNotification = () => {
    const context = useContext(ErrorNotificationContext);
    if (!context) {
        throw new Error('useErrorNotification must be used within ErrorNotificationProvider');
    }
    return context;
};

export const ErrorNotificationProvider = ({ children }) => {
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'error', // 'error', 'warning', 'info', 'success'
    });

    const showError = useCallback((message, severity = 'error') => {
        setNotification({
            open: true,
            message: message,
            severity: severity,
        });
    }, []);

    // Set the global error handler when component mounts
    useEffect(() => {
        setGlobalErrorHandler(showError);
        return () => {
            setGlobalErrorHandler(null);
        };
    }, [showError]);

    const handleClose = useCallback((event, reason) => {
        // Don't close on clickaway - only on timeout or manual close
        if (reason === 'clickaway') {
            return;
        }
        setNotification((prev) => ({ ...prev, open: false }));
    }, []);

    return (
        <ErrorNotificationContext.Provider value={{ showError }}>
            {children}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                    top: '80px !important', // Position below header
                    zIndex: 9999, // Ensure it's above other content
                }}
            >
                <Alert
                    onClose={handleClose}
                    severity={notification.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        minWidth: '300px',
                        maxWidth: '500px',
                        '& .MuiAlert-message': {
                            fontSize: '14px',
                            fontWeight: 500,
                        },
                    }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </ErrorNotificationContext.Provider>
    );
};

